/**
 * FP3-033 — Provider Schedule (Display).
 *
 * Big landscape-friendly board view of one week. Reuses the same
 * /api/schedule data path as the print view; presentation is the
 * only difference.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import {
  DisplayShell,
  PrintAccessGate,
  classifyError,
} from "@/components/print/PrintShell";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function todayIso() { return new Date().toISOString().slice(0, 10); }
function startOfWeek(iso) {
  const d = new Date(iso);
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().slice(0, 10);
}
function addDays(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function DisplayProviderSchedule() {
  const { providerId } = useParams();
  const [searchParams] = useSearchParams();
  const date = searchParams.get("date") || todayIso();
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [sessions, setSessions] = useState([]);

  const start = useMemo(() => startOfWeek(date), [date]);
  const end = useMemo(() => addDays(start, 6), [start]);

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    api.getSchedule({ provider_id: providerId, start_date: start, end_date: end })
      .then((r) => {
        if (cancelled) return;
        setSessions(r.data || []);
        setPhase("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message);
        setPhase(classifyError(err));
      });
    return () => { cancelled = true; };
  }, [providerId, start, end]);

  const grouped = useMemo(() => {
    const out = {};
    for (const s of sessions) {
      const d = s.date || "—";
      out[d] = out[d] || [];
      out[d].push(s);
    }
    Object.values(out).forEach((rows) =>
      rows.sort((a, b) => String(a.start_time || "").localeCompare(String(b.start_time || "")))
    );
    return out;
  }, [sessions]);

  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) out.push(addDays(start, i));
    return out;
  }, [start]);

  return (
    <DisplayShell
      title="Provider Schedule"
      subtitle={`Week of ${start}`}
      testId="display-provider-schedule"
    >
      <PrintAccessGate phase={phase} error={error}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 12,
            maxWidth: 1400,
            margin: "0 auto",
          }}
        >
          {days.map((dIso) => {
            const rows = grouped[dIso] || [];
            const d = new Date(dIso);
            return (
              <div
                key={dIso}
                data-testid={`display-schedule-day-${dIso}`}
                style={{
                  background: "#fff",
                  border: "1px solid var(--fp-print-line)",
                  borderRadius: 12,
                  padding: 14,
                  minHeight: 240,
                }}
              >
                <div style={{ marginBottom: 10 }}>
                  <div className="fp-display-label">{DAYS[d.getDay()]}</div>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>
                    {dIso.slice(5)}
                  </div>
                </div>
                {rows.length === 0 ? (
                  <div style={{ color: "var(--fp-print-soft)", fontSize: 13 }}>
                    No sessions
                  </div>
                ) : (
                  rows.map((s) => {
                    const time =
                      s.start_time && s.end_time
                        ? `${s.start_time}–${s.end_time}`
                        : s.start_time || "";
                    const students = (s.students || [])
                      .map((st) => `${st.first_name || ""} ${st.last_name || ""}`.trim())
                      .filter(Boolean);
                    const label = students.length > 0
                      ? students.join(", ")
                      : (s.label || s.title || "Personal");
                    return (
                      <div
                        key={`${s.id || ""}-${s.date}-${s.start_time}`}
                        style={{
                          background: "var(--fp-print-bg-soft)",
                          borderLeft: "3px solid var(--fp-print-accent)",
                          borderRadius: 8,
                          padding: "8px 10px",
                          marginBottom: 8,
                          fontSize: 14,
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>{time}</div>
                        <div style={{ fontSize: 13 }}>{label}</div>
                        {s.service_area && (
                          <div
                            className="fp-display-label"
                            style={{ marginTop: 2, fontSize: 10 }}
                          >
                            {s.service_area}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            );
          })}
        </div>
      </PrintAccessGate>
    </DisplayShell>
  );
}
